/**
 * qiblaCardStrings.js
 * ------------------
 * Localized copy specific to the new QiblaCard component. Kept here
 * (rather than injected into i18n.js) so future edits are scoped to
 * this single feature and don't risk merge conflicts with the app-
 * wide translations.
 */

export const QIBLA_STRINGS = {
  aligned: {
    ar: 'أنت في الاتجاه الصحيح',
    en: 'You are in the right direction',
    id: 'Anda menghadap arah yang benar',
    ur: 'آپ درست سمت میں ہیں',
    fr: 'Vous êtes dans la bonne direction',
    bn: 'আপনি সঠিক দিকে আছেন',
    zh: '您正朝向正确的方向',
    fa: 'شما در جهت صحیح هستید',
    tr: 'Doğru yöndesiniz',
    es: 'Estás en la dirección correcta',
    pt: 'Você está na direção certa',
    de: 'Sie sind in der richtigen Richtung',
    it: 'Sei nella direzione giusta',
  },
  guidance: {
    ar: 'وجّه الهاتف نحو القبلة',
    en: 'Turn the phone toward the Qibla',
    id: 'Arahkan ponsel ke Kiblat',
    ur: 'فون کو قبلہ کی طرف موڑیں',
    fr: 'Tournez le téléphone vers la Qibla',
    bn: 'ফোনটি কিবলার দিকে ঘোরান',
    zh: '将手机转向朝向',
    fa: 'گوشی را به سمت قبله بچرخانید',
    tr: 'Telefonu Kıble\'ye doğru çevirin',
    es: 'Gira el teléfono hacia la Qibla',
    pt: 'Vire o telefone em direção à Qibla',
    de: 'Drehen Sie das Telefon zur Qibla',
    it: 'Gira il telefono verso la Qibla',
  },
  turnLeft: {
    ar: 'أدر إلى اليسار',
    en: 'Turn left',
    id: 'Putar ke kiri',
    ur: 'بائیں مڑیں',
    fr: 'Tournez à gauche',
    bn: 'বাঁয়ে ঘুরুন',
    zh: '向左转',
    fa: 'به چپ بچرخانید',
    tr: 'Sola çevirin',
    es: 'Gira a la izquierda',
    pt: 'Vire à esquerda',
    de: 'Nach links drehen',
    it: 'Gira a sinistra',
  },
  turnRight: {
    ar: 'أدر إلى اليمين',
    en: 'Turn right',
    id: 'Putar ke kanan',
    ur: 'دائیں مڑیں',
    fr: 'Tournez à droite',
    bn: 'ডানে ঘুরুন',
    zh: '向右转',
    fa: 'به راست بچرخانید',
    tr: 'Sağa çevirin',
    es: 'Gira a la derecha',
    pt: 'Vire à direita',
    de: 'Nach rechts drehen',
    it: 'Gira a destra',
  },
  muteChime: {
    ar: 'كتم النغمة',
    en: 'Mute chime',
    id: 'Bisukan nada',
    ur: 'گھنٹی خاموش کریں',
    fr: 'Couper le son',
    bn: 'ধ্বনি বন্ধ করুন',
    zh: '静音提示',
    fa: 'قطع صدای زنگ',
    tr: 'Sesi kapat',
    es: 'Silenciar sonido',
    pt: 'Silenciar som',
    de: 'Ton stummschalten',
    it: 'Disattiva suono',
  },
  unmuteChime: {
    ar: 'تشغيل النغمة',
    en: 'Enable chime',
    id: 'Aktifkan nada',
    ur: 'گھنٹی چالو کریں',
    fr: 'Activer le son',
    bn: 'ধ্বনি চালু করুন',
    zh: '开启提示音',
    fa: 'فعال کردن صدای زنگ',
    tr: 'Sesi aç',
    es: 'Activar sonido',
    pt: 'Ativar som',
    de: 'Ton aktivieren',
    it: 'Attiva suono',
  },
  enableCompass: {
    ar: 'اضغط لتفعيل البوصلة',
    en: 'Tap to enable compass',
    id: 'Ketuk untuk mengaktifkan kompas',
    ur: 'کمپاس فعال کرنے کے لیے ٹیپ کریں',
    fr: 'Toucher pour activer la boussole',
    bn: 'কম্পাস সক্রিয় করতে ট্যাপ করুন',
    zh: '轻触启用指南针',
    fa: 'برای فعال‌سازی قطب‌نما بزنید',
    tr: 'Pusulayı etkinleştirmek için dokunun',
    es: 'Toca para activar la brújula',
    pt: 'Toque para ativar a bússola',
    de: 'Tippen, um den Kompass zu aktivieren',
    it: 'Tocca per attivare la bussola',
  },
  compassUnsupported: {
    ar: 'البوصلة غير مدعومة على هذا الجهاز',
    en: 'Compass not supported on this device',
    id: 'Kompas tidak didukung di perangkat ini',
    ur: 'اس آلے پر کمپاس معاون نہیں',
    fr: 'Boussole non prise en charge sur cet appareil',
    bn: 'এই ডিভাইসে কম্পাস সমর্থিত নয়',
    zh: '此设备不支持指南针',
    fa: 'قطب‌نما در این دستگاه پشتیبانی نمی‌شود',
    tr: 'Bu cihazda pusula desteklenmiyor',
    es: 'La brújula no está disponible en este dispositivo',
    pt: 'A bússola não é suportada neste dispositivo',
    de: 'Der Kompass wird auf diesem Gerät nicht unterstützt',
    it: 'La bussola non è supportata su questo dispositivo',
  },
  hintTitle: {
    ar: 'كيف تستخدم البطاقة',
    en: 'How to use this card',
    id: 'Cara menggunakan kartu ini',
    ur: 'یہ کارڈ کیسے استعمال کریں',
    fr: 'Comment utiliser cette carte',
    bn: 'এই কার্ডটি কীভাবে ব্যবহার করবেন',
    zh: '如何使用此卡',
    fa: 'نحوه استفاده از این کارت',
    tr: 'Bu kartı nasıl kullanırım',
    es: 'Cómo usar esta tarjeta',
    pt: 'Como usar este cartão',
    de: 'So verwenden Sie diese Karte',
    it: 'Come usare questa scheda',
  },
  hintBody: {
    ar: [
      'ضع الهاتف على سطح أفقي مستوٍ.',
      'دَوّر الهاتف بأيّ اتّجاه حتى تظهر صورة الكعبة المشرّفة.',
      'عند ظهور الصورة يكون هاتفك متّجهاً نحو القبلة.',
      'اتّبع هذا الاتّجاه باطمئنان.',
    ],
    en: [
      'Place the phone on a flat, horizontal surface.',
      'Rotate the phone in any direction until the image of the honored Kaaba appears.',
      'When the image appears, your phone is pointing toward the Qibla.',
      'Follow that direction with confidence.',
    ],
    id: [
      'Letakkan ponsel pada permukaan datar dan horizontal.',
      'Putar ponsel ke arah mana pun sampai gambar Kabah muncul.',
      'Saat gambar muncul, ponsel Anda mengarah ke Kiblat.',
      'Ikuti arah tersebut dengan tenang.',
    ],
    ur: [
      'فون کو ایک ہموار افقی سطح پر رکھیں۔',
      'فون کو کسی بھی سمت میں گھمائیں یہاں تک کہ کعبۃ اللہ کی تصویر ظاہر ہو۔',
      'جب تصویر نمودار ہو تو آپ کا فون قبلہ کی سمت میں ہے۔',
      'اطمینان سے اس سمت کی پیروی کریں۔',
    ],
    fr: [
      'Posez le téléphone sur une surface plane et horizontale.',
      "Faites-le tourner dans n'importe quelle direction jusqu'à ce que l'image de la Kaaba apparaisse.",
      "Lorsque l'image apparaît, votre téléphone est orienté vers la Qibla.",
      'Suivez cette direction en toute confiance.',
    ],
    bn: [
      'ফোনটিকে একটি সমতল অনুভূমিক পৃষ্ঠে রাখুন।',
      'যতক্ষণ না কাবার ছবি দেখা যায় ততক্ষণ ফোনটিকে যেকোনো দিকে ঘোরান।',
      'ছবি দেখা দিলে বুঝবেন ফোনটি কিবলার দিকে আছে।',
      'নিশ্চিন্তে সেই দিকে অনুসরণ করুন।',
    ],
    zh: [
      '将手机平放在水平面上。',
      '向任意方向旋转手机，直到出现天房的图像。',
      '当图像出现时，您的手机正朝向朝向 (Qibla)。',
      '请安心地跟随这个方向。',
    ],
    fa: [
      'گوشی را روی سطحی صاف و افقی قرار دهید.',
      'گوشی را در هر جهتی بچرخانید تا تصویر کعبه ظاهر شود.',
      'وقتی تصویر ظاهر شد، گوشی شما رو به قبله است.',
      'با اطمینان از این جهت پیروی کنید.',
    ],
    tr: [
      'Telefonu düz ve yatay bir yüzeye koyun.',
      "Kâbe'nin görüntüsü belirene kadar telefonu herhangi bir yöne çevirin.",
      'Görüntü belirdiğinde telefonunuz Kıble yönündedir.',
      'O yöne güvenle uyun.',
    ],
    es: [
      'Coloca el teléfono sobre una superficie plana y horizontal.',
      'Gira el teléfono en cualquier dirección hasta que aparezca la imagen de la Kaaba.',
      'Cuando aparezca la imagen, tu teléfono apunta hacia la Qibla.',
      'Sigue esa dirección con confianza.',
    ],
    pt: [
      'Coloque o telefone numa superfície plana e horizontal.',
      'Gire o telefone em qualquer direção até que a imagem da Caaba apareça.',
      'Quando a imagem aparecer, o seu telefone aponta para a Qibla.',
      'Siga essa direção com confiança.',
    ],
    de: [
      'Legen Sie das Telefon auf eine ebene, waagerechte Fläche.',
      'Drehen Sie das Telefon in eine beliebige Richtung, bis das Bild der ehrwürdigen Kaaba erscheint.',
      'Wenn das Bild erscheint, zeigt Ihr Telefon in Richtung der Qibla.',
      'Folgen Sie dieser Richtung mit Zuversicht.',
    ],
    it: [
      'Appoggia il telefono su una superficie piana e orizzontale.',
      "Ruota il telefono in qualsiasi direzione finché non appare l'immagine della Kaaba.",
      "Quando l'immagine appare, il tuo telefono è rivolto verso la Qibla.",
      'Segui quella direzione con serenità.',
    ],
  },
  helpHint: {
    ar: 'المساعدة',
    en: 'Help',
    id: 'Bantuan',
    ur: 'مدد',
    fr: 'Aide',
    bn: 'সাহায্য',
    zh: '帮助',
    fa: 'راهنما',
    tr: 'Yardım',
    es: 'Ayuda',
    pt: 'Ajuda',
    de: 'Hilfe',
    it: 'Aiuto',
  },
  directionLabel: {
    ar: 'الاتجاه من الشمال الحقيقي',
    en: 'Direction from True North',
    id: 'Arah dari Utara Sejati',
    ur: 'حقیقی شمال سے سمت',
    fr: 'Direction depuis le Nord vrai',
    bn: 'সত্য উত্তর থেকে দিক',
    zh: '从真北方向',
    fa: 'جهت از شمال حقیقی',
    tr: 'Gerçek Kuzey\'den yön',
    es: 'Dirección desde el Norte Verdadero',
    pt: 'Direção a partir do Norte Verdadeiro',
    de: 'Richtung vom geografischen Norden',
    it: 'Direzione dal Nord Geografico',
  },
  distanceLabel: {
    ar: 'المسافة إلى الكعبة المشرّفة',
    en: 'Distance to the Kaaba',
    id: 'Jarak ke Kabah',
    ur: 'کعبہ تک فاصلہ',
    fr: 'Distance à la Kaaba',
    bn: 'কাবা পর্যন্ত দূরত্ব',
    zh: '到天房的距离',
    fa: 'فاصله تا کعبه',
    tr: 'Kâbe\'ye uzaklık',
    es: 'Distancia a la Kaaba',
    pt: 'Distância à Caaba',
    de: 'Entfernung zur Kaaba',
    it: 'Distanza dalla Kaaba',
  },
  bubbleHint: {
    ar: 'استوِ الفقاعة في المركز — يضمن أن الهاتف مستقرّ على سطح مستوٍ',
    en: 'Center the bubble — ensures the phone rests on a level surface',
    id: 'Tempatkan gelembung di tengah — memastikan ponsel berada pada permukaan datar',
    ur: 'بلبلے کو مرکز میں لائیں — یقینی بناتا ہے کہ فون ہموار سطح پر ہے',
    fr: 'Centrez la bulle — garantit que le téléphone repose sur une surface plane',
    bn: 'বুদবুদটি কেন্দ্রে রাখুন — নিশ্চিত করে যে ফোনটি সমতল পৃষ্ঠে আছে',
    zh: '将气泡居中 — 确保手机置于水平面上',
    fa: 'حباب را در مرکز قرار دهید — اطمینان می‌دهد گوشی بر سطحی صاف است',
    tr: 'Baloncuğu ortalayın — telefonun düz bir yüzeyde durduğundan emin olun',
    es: 'Centra la burbuja — garantiza que el teléfono está sobre una superficie plana',
    pt: 'Centralize a bolha — garante que o telefone está numa superfície plana',
    de: 'Blase mittig ausrichten — stellt sicher, dass das Telefon eben liegt',
    it: 'Centra la bolla — assicura che il telefono sia su una superficie piana',
  },
  calibTitle: {
    ar: 'قراءة البوصلة غير دقيقة الآن',
    en: 'Compass reading is inaccurate right now',
    id: 'Pembacaan kompas sedang tidak akurat',
    ur: 'کمپاس کی ریڈنگ ابھی درست نہیں',
    fr: 'La boussole est imprécise pour le moment',
    bn: 'কম্পাসের রিডিং এখন সঠিক নয়',
    zh: '指南针读数目前不准确',
    fa: 'خوانش قطب‌نما اکنون دقیق نیست',
    tr: 'Pusula şu anda doğru ölçmüyor',
    es: 'La brújula no es precisa en este momento',
    pt: 'A bússola está imprecisa agora',
    de: 'Der Kompass ist gerade ungenau',
    it: 'La bussola non è precisa in questo momento',
  },
  calibIntro: {
    ar: 'عايرها لتحديد القبلة بدقة:',
    en: 'Calibrate it to find the Qibla precisely:',
    id: 'Kalibrasi agar arah Kiblat tepat:',
    ur: 'قبلہ کی درست سمت کے لیے اسے کیلیبریٹ کریں:',
    fr: 'Calibrez-la pour trouver la Qibla avec précision :',
    bn: 'সঠিকভাবে কিবলা খুঁজতে এটি ক্যালিব্রেট করুন:',
    zh: '请校准以准确找到朝向：',
    fa: 'برای یافتن دقیق قبله آن را کالیبره کنید:',
    tr: 'Kıble\'yi doğru bulmak için kalibre edin:',
    es: 'Calíbrala para encontrar la Qibla con precisión:',
    pt: 'Calibre-a para encontrar a Qibla com precisão:',
    de: 'Kalibrieren Sie ihn, um die Qibla genau zu finden:',
    it: 'Calibrala per trovare la Qibla con precisione:',
  },
  calibSteps: {
    ar: ['حرّك الهاتف ببطء على شكل رقم 8 مرتين أو ثلاثاً', 'ابتعد عن المعادن والأجهزة الكهربائية والسيارة', 'انزع غطاء الهاتف إن كان فيه مغناطيس'],
    en: ['Slowly move the phone in a figure-8, two or three times', 'Stay away from metal, electrical devices and cars', 'Remove the phone case if it has a magnet'],
    id: ['Gerakkan ponsel perlahan membentuk angka 8, dua atau tiga kali', 'Jauhi logam, perangkat listrik, dan mobil', 'Lepaskan casing ponsel jika ada magnetnya'],
    ur: ['فون کو آہستہ آہستہ دو یا تین بار 8 کی شکل میں گھمائیں', 'دھات، برقی آلات اور گاڑی سے دور رہیں', 'اگر فون کور میں مقناطیس ہو تو اسے اتار دیں'],
    fr: ['Déplacez lentement le téléphone en forme de 8, deux ou trois fois', 'Éloignez-vous du métal, des appareils électriques et des voitures', 'Retirez la coque si elle contient un aimant'],
    bn: ['ফোনটি ধীরে ধীরে দুই-তিনবার ৮ আকারে ঘোরান', 'ধাতু, বৈদ্যুতিক যন্ত্র ও গাড়ি থেকে দূরে থাকুন', 'ফোনের কভারে চুম্বক থাকলে তা খুলে ফেলুন'],
    zh: ['将手机缓慢地画"8"字两三次', '远离金属、电器和汽车', '如手机壳带磁铁，请取下'],
    fa: ['گوشی را آهسته دو یا سه بار به شکل عدد ۸ حرکت دهید', 'از فلزات، وسایل برقی و خودرو دور شوید', 'اگر قاب گوشی آهنربا دارد آن را بردارید'],
    tr: ['Telefonu yavaşça iki üç kez 8 şeklinde hareket ettirin', 'Metal, elektrikli cihaz ve arabalardan uzak durun', 'Mıknatıslıysa telefon kılıfını çıkarın'],
    es: ['Mueve el teléfono despacio formando un 8, dos o tres veces', 'Aléjate de metales, aparatos eléctricos y coches', 'Quita la funda si tiene un imán'],
    pt: ['Mova o telefone devagar em forma de 8, duas ou três vezes', 'Afaste-se de metais, aparelhos elétricos e carros', 'Retire a capa se ela tiver ímã'],
    de: ['Bewegen Sie das Telefon langsam zwei- bis dreimal in Form einer 8', 'Halten Sie Abstand zu Metall, Elektrogeräten und Autos', 'Entfernen Sie die Hülle, falls sie einen Magneten hat'],
    it: ['Muovi lentamente il telefono a forma di 8, due o tre volte', 'Allontanati da metalli, apparecchi elettrici e auto', 'Togli la custodia se contiene un magnete'],
  },
  calibGotIt: {
    ar: 'فهمت', en: 'Got it', id: 'Mengerti', ur: 'سمجھ گیا', fr: 'Compris', bn: 'বুঝেছি', zh: '知道了',
    fa: 'متوجه شدم', tr: 'Anladım', es: 'Entendido', pt: 'Entendi', de: 'Verstanden', it: 'Ho capito',
  },
  calibNever: {
    ar: 'لا تُظهر هذا التنبيه مجدداً',
    en: 'Don\'t show this tip again',
    id: 'Jangan tampilkan lagi',
    ur: 'یہ اشارہ دوبارہ نہ دکھائیں',
    fr: 'Ne plus afficher ce conseil',
    bn: 'এই পরামর্শ আর দেখাবেন না',
    zh: '不再显示此提示',
    fa: 'این راهنما را دیگر نشان نده',
    tr: 'Bu ipucunu bir daha gösterme',
    es: 'No volver a mostrar este aviso',
    pt: 'Não mostrar este aviso novamente',
    de: 'Diesen Hinweis nicht mehr anzeigen',
    it: 'Non mostrare più questo avviso',
  },
  calibReady: {
    ar: 'البوصلة جاهزة', en: 'Compass is ready', id: 'Kompas siap', ur: 'کمپاس تیار ہے', fr: 'La boussole est prête',
    bn: 'কম্পাস প্রস্তুত', zh: '指南针已就绪', fa: 'قطب‌نما آماده است', tr: 'Pusula hazır', es: 'La brújula está lista',
    pt: 'A bússola está pronta', de: 'Kompass ist bereit', it: 'La bussola è pronta',
  },
  calibToggle: {
    ar: 'تنبيه معايرة البوصلة',
    en: 'Compass calibration tip',
    id: 'Pengingat kalibrasi kompas',
    ur: 'کمپاس کیلیبریشن کا اشارہ',
    fr: 'Conseil de calibrage de la boussole',
    bn: 'কম্পাস ক্যালিব্রেশন পরামর্শ',
    zh: '指南针校准提示',
    fa: 'هشدار کالیبره کردن قطب‌نما',
    tr: 'Pusula kalibrasyon uyarısı',
    es: 'Aviso de calibración de la brújula',
    pt: 'Aviso de calibração da bússola',
    de: 'Hinweis zur Kompass-Kalibrierung',
    it: 'Avviso di calibrazione della bussola',
  },
  calibHowTo: {
    ar: 'كيف أعاير البوصلة؟',
    en: 'How do I calibrate the compass?',
    id: 'Bagaimana cara mengkalibrasi kompas?',
    ur: 'کمپاس کیسے کیلیبریٹ کریں؟',
    fr: 'Comment calibrer la boussole ?',
    bn: 'কীভাবে কম্পাস ক্যালিব্রেট করব?',
    zh: '如何校准指南针？',
    fa: 'چگونه قطب‌نما را کالیبره کنم؟',
    tr: 'Pusula nasıl kalibre edilir?',
    es: '¿Cómo calibro la brújula?',
    pt: 'Como calibrar a bússola?',
    de: 'Wie kalibriere ich den Kompass?',
    it: 'Come calibro la bussola?',
  },
};

/**
 * Pick a string in the user's language, falling back to English then Arabic.
 */
export const q = (key, lang) => {
  const bucket = QIBLA_STRINGS[key];
  if (!bucket) return '';
  return bucket[lang] || bucket.en || bucket.ar || '';
};
